import { useCallback, useEffect, useState } from "react";
import { Search, Download, Trash2, StickyNote, History } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { LeadTimeline } from "./LeadTimeline";

export const STATUSES = ["new", "contacted", "approved", "onboarded", "rejected"];
const BADGE = { new: "bg-sky-400/15 text-sky-300", contacted: "bg-amber-400/15 text-amber-300", approved: "bg-emerald/15 text-emerald-300", onboarded: "bg-saffron/15 text-orange-300", rejected: "bg-red-400/15 text-red-300" };
const ROLE = { retailer: "Retailer", distributor: "Distributor", shg: "Individual / SHG" };

const Filter = ({ value, onChange, items, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-full bg-card sm:w-44" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const LeadsPanel = ({ isAdmin = true }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [role, setRole] = useState("all");
  const [assignee, setAssignee] = useState("all");
  const [team, setTeam] = useState([]);
  const [note, setNote] = useState(null);
  const [histId, setHistId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/leads", { params: { q, status, role, assignee } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(errMsg(e)));
  }, [q, status, role, assignee]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    api.get("/admin/team").then((r) => setTeam(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    return undefined;
  }, [isAdmin]);

  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const update = async (id, body) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, body);
      setRows((p) => p.map((r) => (r.id === id ? data : r)));
      toast.success("Lead updated");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setRows((p) => p.filter((r) => r.id !== id));
      toast.success("Lead deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/leads/export", { params: { q, status, role, assignee }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "indianpayhub-leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 sm:min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, mobile, pincode, ref…" className="h-10 w-full rounded-lg border border-white/10 bg-card pl-9 pr-3 text-sm outline-none focus:border-saffron" data-testid="leads-search-input" />
        </div>
        <Filter value={status} onChange={setStatus} testid="leads-status-filter" items={[["all", "All statuses"], ...STATUSES.map((s) => [s, s[0].toUpperCase() + s.slice(1)])]} />
        <Filter value={role} onChange={setRole} testid="leads-role-filter" items={[["all", "All types"], ...Object.entries(ROLE)]} />
        {isAdmin && <Filter value={assignee} onChange={setAssignee} testid="leads-assignee-filter" items={[["all", "Everyone"], ["none", "Unassigned"], ...team.map((m) => [m.id, m.name])]} />}
        <button onClick={exportCsv} className="btn-primary justify-center" data-testid="leads-export-btn"><Download size={16} /> Export CSV</button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full min-w-[1080px] text-sm">
          <thead className="bg-card text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["Ref", "Name", "Mobile", "Type", "Location", "Date", "Status", ...(isAdmin ? ["Assigned to"] : []), ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-white/[0.02]" data-testid={`lead-row-${r.ref}`}>
                <td className="px-4 py-3 font-mono text-xs text-orange-300">{r.ref}</td>
                <td className="px-4 py-3"><p className="font-medium text-white">{r.name}</p><p className="text-xs text-slate-400">{r.business || "—"}</p></td>
                <td className="px-4 py-3 font-mono">
                  <a href={`tel:${r.mobile}`} className="hover:text-saffron">{r.mobile}</a>
                  {r.mobile_verified && <span className="ml-2 rounded bg-emerald/15 px-1.5 py-0.5 font-sans text-[10px] text-emerald-300" data-testid={`lead-verified-${r.ref}`}>OTP ✓</span>}
                  {r.alerts && <p className="mt-1 font-sans text-[11px] text-slate-400">Alerts: email {r.alerts.email} · WA {r.alerts.whatsapp}</p>}
                </td>
                <td className="px-4 py-3">{ROLE[r.role] ?? r.role}</td>
                <td className="px-4 py-3">{r.state} · <span className="font-mono text-slate-400">{r.pincode}</span></td>
                <td className="px-4 py-3 text-slate-400">{new Date(r.created_at).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3">
                  <Select value={r.status} onValueChange={(v) => update(r.id, { status: v })}>
                    <SelectTrigger className={`h-8 w-32 border-0 capitalize ${BADGE[r.status]}`} data-testid={`lead-status-${r.ref}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                {isAdmin && <td className="px-4 py-3">
                  <Select value={r.assigned_to || "none"} onValueChange={(v) => update(r.id, { assigned_to: v === "none" ? "" : v })}>
                    <SelectTrigger className={`h-8 w-36 bg-card ${r.assigned_to ? "text-white" : "text-slate-400"}`} data-testid={`lead-assign-${r.ref}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>}
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setHistId(r.id)} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white" data-testid={`lead-history-btn-${r.ref}`} aria-label="Activity"><History size={16} /></button>
                    <button onClick={() => setNote({ id: r.id, text: r.notes || "", name: r.name })} className={`rounded-lg p-2 hover:bg-white/5 ${r.notes ? "text-saffron" : "text-slate-400"}`} data-testid={`lead-note-btn-${r.ref}`} aria-label="Notes"><StickyNote size={16} /></button>
                    {isAdmin && <button onClick={() => remove(r.id)} className="rounded-lg p-2 text-slate-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`lead-delete-btn-${r.ref}`} aria-label="Delete"><Trash2 size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400" data-testid="leads-empty">No leads found</td></tr>}
          </tbody>
        </table>
      </div>

      <LeadTimeline lead={rows.find((r) => r.id === histId)} onClose={() => setHistId(null)} />
      <Dialog open={!!note} onOpenChange={(o) => !o && setNote(null)}>
        <DialogContent className="border-white/10 bg-card text-white">
          <DialogHeader><DialogTitle>Notes · {note?.name}</DialogTitle></DialogHeader>
          <Textarea rows={5} value={note?.text ?? ""} onChange={(e) => setNote({ ...note, text: e.target.value })} data-testid="lead-note-textarea" />
          <button className="btn-primary justify-center" data-testid="lead-note-save-btn" onClick={async () => { await update(note.id, { notes: note.text }); setNote(null); }}>Save note</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
